import { supabase } from '../utils/supabase';
import { createLogger } from '../utils/logger';
import { format, startOfWeek, differenceInDays } from 'date-fns';

const logger = createLogger({ module: 'InternshipNotificationService' });

interface InternshipNotificationData {
  userId: string;
  focusId: string;
  company?: string;
  weekNumber?: number;
  totalWeeks?: number;
}

interface PhaseInfo {
  name: string;
  emoji: string;
  description: string;
}

export class InternshipNotificationService {
  private static instance: InternshipNotificationService;

  static getInstance(): InternshipNotificationService {
    if (!InternshipNotificationService.instance) {
      InternshipNotificationService.instance = new InternshipNotificationService();
    }
    return InternshipNotificationService.instance;
  }

  /**
   * Send a notification when user completes weekly assessment
   */
  async sendAssessmentCompleteNotification(data: InternshipNotificationData & { streak?: number }) {
    try {
      // Check if this creates a streak
      if (data.streak && data.streak >= 3) {
        await this.createNotificationEvent({
          templateKey: 'assessment_streak',
          userId: data.userId,
          metadata: {
            streak_count: data.streak.toString(),
            focus_id: data.focusId
          }
        });
      }

      logger.info('Assessment completion notification sent', { userId: data.userId, streak: data.streak });
    } catch (error) {
      logger.error('Failed to send assessment complete notification', error);
    }
  }

  /**
   * Send a notification when user achieves a goal
   */
  async sendGoalAchievedNotification(data: InternshipNotificationData & { goalText: string; goalIndex: number }) {
    try {
      await this.createNotificationEvent({
        templateKey: 'goal_achieved',
        userId: data.userId,
        metadata: {
          goal_text: data.goalText,
          goal_index: data.goalIndex.toString(),
          focus_id: data.focusId
        }
      });

      logger.info('Goal achieved notification sent', { userId: data.userId, goalIndex: data.goalIndex });
    } catch (error) {
      logger.error('Failed to send goal achieved notification', error);
    }
  }

  /**
   * Send a notification when user enters a new phase
   */
  async sendPhaseTransitionNotification(data: InternshipNotificationData & { phase: PhaseInfo; previousPhase: string }) {
    try {
      // Don't send for onboarding phase (it's the starting phase)
      if (data.phase.name.toLowerCase() === 'onboarding') return;

      await this.createNotificationEvent({
        templateKey: 'internship_phase_transition',
        userId: data.userId,
        metadata: {
          phase_name: data.phase.name,
          phase_emoji: data.phase.emoji,
          phase_description: data.phase.description,
          focus_id: data.focusId,
          previous_phase: data.previousPhase
        }
      });

      logger.info('Phase transition notification sent', { 
        userId: data.userId, 
        phase: data.phase.name,
        previousPhase: data.previousPhase 
      });
    } catch (error) {
      logger.error('Failed to send phase transition notification', error);
    }
  }

  /**
   * Send internship completion notification
   */
  async sendInternshipCompletionNotification(data: InternshipNotificationData) {
    try {
      await this.createNotificationEvent({
        templateKey: 'internship_completed',
        userId: data.userId,
        metadata: {
          company: data.company || 'your company',
          total_weeks: data.totalWeeks?.toString() || '0',
          focus_id: data.focusId
        }
      });

      logger.info('Internship completion notification sent', { userId: data.userId });
    } catch (error) {
      logger.error('Failed to send internship completion notification', error);
    }
  }

  /**
   * Check and send evidence gap reminder if needed
   */
  async checkAndSendEvidenceGapReminder(data: InternshipNotificationData & { undocumentedGoals: number }) {
    try {
      // Only send if there are multiple undocumented goals
      if (data.undocumentedGoals < 2) return;

      // Check if we already sent this reminder this week
      const lastReminder = await this.getLastNotificationOfType(data.userId, 'evidence_gap_reminder');
      if (lastReminder && differenceInDays(new Date(), new Date(lastReminder.created_at)) < 7) {
        return;
      }

      await this.createNotificationEvent({
        templateKey: 'evidence_gap_reminder',
        userId: data.userId,
        metadata: {
          undocumented_goals: data.undocumentedGoals.toString(),
          focus_id: data.focusId
        }
      });

      logger.info('Evidence gap reminder sent', { userId: data.userId, undocumentedGoals: data.undocumentedGoals });
    } catch (error) {
      logger.error('Failed to send evidence gap reminder', error);
    }
  }

  /**
   * Check activity and send mid-week reminder if needed
   */
  async checkAndSendActivityReminder(data: InternshipNotificationData & { daysInactive: number }) {
    try {
      // Only send if inactive for 3+ days
      if (data.daysInactive < 3) return;

      // Check if we already sent this reminder this week
      const lastReminder = await this.getLastNotificationOfType(data.userId, 'midweek_activity_reminder');
      if (lastReminder && differenceInDays(new Date(), new Date(lastReminder.created_at)) < 7) {
        return;
      }

      await this.createNotificationEvent({
        templateKey: 'midweek_activity_reminder',
        userId: data.userId,
        metadata: {
          days_inactive: data.daysInactive.toString(),
          focus_id: data.focusId
        }
      });

      logger.info('Activity reminder sent', { userId: data.userId, daysInactive: data.daysInactive });
    } catch (error) {
      logger.error('Failed to send activity reminder', error);
    }
  }

  /**
   * Create a notification event that will be processed by the notification system
   */
  private async createNotificationEvent(data: {
    templateKey: string;
    userId: string;
    metadata: Record<string, string>;
  }) {
    const { data: event, error } = await supabase
      .from('notification_events')
      .insert({
        event_type: 'notification.requested',
        user_id: data.userId,
        metadata: {
          template_key: data.templateKey,
          ...data.metadata
        },
        status: 'pending'
      })
      .select()
      .single();

    if (error) {
      throw error;
    }

    logger.debug('Notification event created', { eventId: event.id, templateKey: data.templateKey });
    return event;
  }

  /**
   * Get the last notification of a specific type for a user
   */
  private async getLastNotificationOfType(userId: string, templateKey: string) {
    const { data, error } = await supabase
      .from('user_notifications')
      .select('created_at')
      .eq('user_id', userId)
      .eq('metadata->template_key', templateKey)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error && error.code !== 'PGRST116') { // Not found is ok
      logger.error('Failed to get last notification', error);
    }

    return data;
  }

  /**
   * Calculate assessment streak for a user
   */
  async calculateAssessmentStreak(focusId: string): Promise<number> {
    try {
      const { data: assessments, error } = await supabase
        .from('internship_assessments')
        .select('week_number, created_at')
        .eq('focus_id', focusId)
        .order('week_number', { ascending: false });

      if (error) throw error;
      if (!assessments || assessments.length === 0) return 0;

      // Check for consecutive weeks
      let streak = 1;
      for (let i = 1; i < assessments.length; i++) {
        if (assessments[i - 1].week_number - assessments[i].week_number === 1) {
          streak++;
        } else {
          break;
        }
      }

      return streak;
    } catch (error) {
      logger.error('Failed to calculate assessment streak', error);
      return 0;
    }
  }

  /**
   * Get phase information based on progress percentage
   */
  getPhaseInfo(progressPercentage: number): PhaseInfo {
    if (progressPercentage <= 15) {
      return {
        name: 'Onboarding',
        emoji: '🟢',
        description: 'Getting familiar with your team and environment. Focus on learning and observing.'
      };
    } else if (progressPercentage <= 35) {
      return {
        name: 'Foundation',
        emoji: '🔵',
        description: 'Building core skills and making your first contributions. Keep learning and growing!'
      };
    } else if (progressPercentage <= 65) {
      return {
        name: 'Growth',
        emoji: '🟣',
        description: 'Taking on bigger challenges and expanding your impact. You\'re hitting your stride!'
      };
    } else if (progressPercentage <= 85) {
      return {
        name: 'Impact',
        emoji: '🟠',
        description: 'Delivering significant value and leading initiatives. Your contributions matter!'
      };
    } else {
      return {
        name: 'Transition',
        emoji: '🔴',
        description: 'Wrapping up projects and preparing for what\'s next. Finish strong!'
      };
    }
  }
}

export const internshipNotificationService = InternshipNotificationService.getInstance();